import React, { useState } from 'react';
import SpringPreview from './SpringPreview';
import { calculateSpringPrice, calculateWireLengthFt } from './priceUtils';
import { computeSpringAttributes } from './physicsUtils';
import RadarChart from './RadarChart';

export interface SpringConfig {
  steelType: string;
  thickness: number;
  length: number;
  diameter: number;
  springIndex: number;
  leftHook: boolean;
  rightHook: boolean;
  hookLength: number;
  baseHookDiameter?: number; // Optional fixed diameter for hooks
}

const defaultConfig: SpringConfig = {
  steelType: 'carbon',
  thickness: 1,
  length: 10,
  diameter: 2,
  springIndex: 8,
  leftHook: true,
  rightHook: true,
  hookLength: 5,
};

const Configurator: React.FC = () => {
  const [config, setConfig] = useState<SpringConfig>(defaultConfig);
  const [quantity, setQuantity] = useState(1);
  const [diameterProfile, setDiameterProfile] = useState<number[]>([2, 2, 2, 2, 2]); // 5 control points, default 2cm
  const profileLength = diameterProfile.length;

  const dimensions = {
    diameter: config.diameter,
    length: config.length,
    coils: config.springIndex,
    leftHook: config.leftHook,
    rightHook: config.rightHook,
    hookLength: config.hookLength,
  };

  const wireLengthFt = calculateWireLengthFt(dimensions);
  const estimatedPrice = calculateSpringPrice(config.thickness, dimensions);
  const attributes = computeSpringAttributes({
    thickness: config.thickness,
    diameter: config.diameter,
    springIndex: config.springIndex,
    length: config.length,
  });
  
  const totalPrice = estimatedPrice * quantity;

  // Handle drag of a control point in the curve editor
  const handleCurveDrag = (idx: number, newY: number) => {
    // Clamp diameter between 0.5 and 50
    const clamped = Math.max(0.5, Math.min(50, newY));
    const updated = diameterProfile.map((d, i) => (i === idx ? clamped : d));
    setDiameterProfile(updated);
  };

  // Optionally, update config.diameter to the average for price/physics
  const avgDiameter = diameterProfile.reduce((a, b) => a + b, 0) / profileLength;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setConfig({
      ...config,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'steelType'
          ? value
          : Number(value),
    });
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuantity(Math.max(1, Number(e.target.value)));
  };

  return (
    <div className="configurator">
      <div className="main-layout">
        <div className="preview-panel">
          <SpringPreview 
            config={{ ...config, diameter: avgDiameter, baseHookDiameter: config.diameter }} 
            diameterProfile={diameterProfile} 
          />
        </div>
        <div className="side-panel">
          <div className="card controls-panel">
            <h2>Spring Parameters</h2>
          <div>
            <label>
              Steel Type:
              <select name="steelType" value={config.steelType} onChange={handleChange}>
                <option value="carbon">Carbon Steel</option>
                <option value="stainless">Stainless Steel</option>
                <option value="alloy">Alloy Steel</option>
              </select>
            </label>
          </div>
          <div>
            <label>
              Thickness (mm):
              <input
                type="number"
                name="thickness"
                value={config.thickness}
                onChange={handleChange}
                min="0.1"
                max="10"
                step="0.1"
              />
            </label>
          </div>
          <div>
            <label>
              Length (cm):
              <input
                type="number"
                name="length"
                value={config.length}
                onChange={handleChange}
                min="1"
                max="100"
                step="0.1"
              />
            </label>
          </div>
          <div>
            <label>
              Hook Length (cm):
              <input
                type="number"
                name="hookLength"
                value={config.hookLength}
                onChange={handleChange}
                min="0"
                max="20"
                step="0.1"
              />
            </label>
          </div>
          <div>
            <label style={{ fontWeight: 'bold', display: 'block', margin: '12px 0 4px' }}>
              Diameter Profile (cm):
            </label>
            <svg width={260} height={80} style={{ background: '#f8f8f8', border: '1px solid #ccc', borderRadius: 4 }}>
              {/* Draw curve */}
              <polyline
                fill="none"
                stroke="#1976d2"
                strokeWidth={2}
                points={diameterProfile.map((d, i) => `${20 + (i * 50)} ${70 - (d - 0.5) * 1.2 * 10}`).join(' ')}
              />
              {/* Draw draggable points */}
              {diameterProfile.map((d, i) => {
                const cx = 20 + i * 50;
                const cy = 70 - (d - 0.5) * 1.2 * 10;
                return (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={7}
                    fill="#fff"
                    stroke="#1976d2"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onMouseDown={e => {
                      const startY = e.clientY;
                      const startVal = d;
                      const onMove = (moveEvt: MouseEvent) => {
                        const dy = moveEvt.clientY - startY;
                        // 12px = 1cm
                        const newVal = startVal - dy / 12;
                        handleCurveDrag(i, newVal);
                      };
                      const onUp = () => {
                        window.removeEventListener('mousemove', onMove);
                        window.removeEventListener('mouseup', onUp);
                      };
                      window.addEventListener('mousemove', onMove);
                      window.addEventListener('mouseup', onUp);
                    }}
                  />
                );
              })}
              {/* X axis labels */}
              {diameterProfile.map((_, i) => (
                <text key={i} x={20 + i * 50} y={75} fontSize={10} textAnchor="middle" fill="#888">
                  {Math.round((i / (profileLength - 1)) * config.length)}cm
                </text>
              ))}
              {/* Y axis min/max */}
              <text x={5} y={70} fontSize={10} fill="#888">0.5</text>
              <text x={5} y={20} fontSize={10} fill="#888">50</text>
            </svg>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              Drag points to shape the diameter along the spring length.
            </div>
          </div>
          <div>
            <label>
              Number of Coils:
              <input
                type="number"
                name="springIndex"
                value={config.springIndex}
                onChange={handleChange}
                min="4"
                max="100"
                step="1"
              />
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
              The total number of coils in the spring.
            </small>
          </div>
          <div>
            <label>
              Hook on Left:
              <input
                type="checkbox"
                name="leftHook"
                checked={config.leftHook}
                onChange={handleChange}
              />
            </label>
          </div>
          <div>
            <label>
              Hook on Right:
              <input
                type="checkbox"
                name="rightHook"
                checked={config.rightHook}
                onChange={handleChange}
              />
            </label>
          </div>
          </div>
          <div className="card price-panel">
            <p>Wire Length: {wireLengthFt.toFixed(2)} ft</p>
            <p>Price per Spring: $ {estimatedPrice.toFixed(2)}</p>
            <div>
              <label>
                Quantity:
                <input type="number" min="1" value={quantity} onChange={handleQuantityChange} />
              </label>
            </div>
            <p>Total: $ {totalPrice.toFixed(2)}</p>
            <button>Checkout</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configurator;
